import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Edit2 } from 'lucide-react';
import { dataRoomAPI } from '../../../services/api';

const PERMISSION_OPTIONS = [
  { value: 'no_access', label: 'No Access' },
  { value: 'view_only', label: 'View Only' },
  { value: 'view_print', label: 'View + Print' },
  { value: 'view_download', label: 'View + Download' },
  { value: 'view_download_print', label: 'View + Download + Print' },
  { value: 'redacted', label: 'Redacted View' },
];

export default function RolesModal({ open, onClose, dataRoomId }) {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', default_permission: 'view_only' });
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    if (open) load();
  }, [open]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await dataRoomAPI.listRoles(dataRoomId);
      setRoles(res.data.data || []);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    try {
      if (editingId) {
        await dataRoomAPI.updateRole(dataRoomId, editingId, form);
      } else {
        await dataRoomAPI.createRole(dataRoomId, form);
      }
      setForm({ name: '', description: '', default_permission: 'view_only' });
      setEditingId(null);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this role?')) return;
    try {
      await dataRoomAPI.deleteRole(dataRoomId, id);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4" data-testid="roles-modal">
      <div className="bg-white rounded-lg w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Manage Roles</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
            <X size={18} />
          </button>
        </div>

        <div className="p-5">
          <form onSubmit={handleSave} className="flex items-end gap-2 mb-4 pb-4 border-b">
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-600 mb-1">Role Name</label>
              <input
                type="text"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="e.g., Legal Counsel"
                className="w-full border rounded px-2 py-1.5 text-sm"
                data-testid="role-name-input"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Default Permission</label>
              <select
                value={form.default_permission}
                onChange={e => setForm({ ...form, default_permission: e.target.value })}
                className="border rounded px-2 py-1.5 text-sm"
                data-testid="role-perm-select"
              >
                {PERMISSION_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
            <button
              type="submit"
              className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
              data-testid="save-role-btn"
            >
              <Plus size={14} className="inline mr-1" />
              {editingId ? 'Update' : 'Add'}
            </button>
            {editingId && (
              <button
                type="button"
                onClick={() => { setForm({ name: '', description: '', default_permission: 'view_only' }); setEditingId(null); }}
                className="px-3 py-1.5 border rounded text-sm"
              >
                Cancel
              </button>
            )}
          </form>

          {loading ? (
            <div className="py-6 text-center text-gray-500 text-sm">Loading...</div>
          ) : (
            <div className="space-y-2">
              {roles.map(r => (
                <div key={r.id} className="flex items-center justify-between border rounded-lg p-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{r.name}</span>
                      {r.is_system && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200">SYSTEM</span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      Default: {PERMISSION_OPTIONS.find(p => p.value === r.default_permission)?.label}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => { setForm({ name: r.name, description: r.description || '', default_permission: r.default_permission }); setEditingId(r.id); }}
                      className="p-1.5 text-gray-400 hover:text-blue-600"
                      data-testid={`edit-role-${r.id}`}
                    >
                      <Edit2 size={14} />
                    </button>
                    {!r.is_system && (
                      <button
                        onClick={() => handleDelete(r.id)}
                        className="p-1.5 text-gray-400 hover:text-red-600"
                        data-testid={`delete-role-${r.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {roles.length === 0 && (
                <div className="py-6 text-center text-gray-500 text-sm">No roles yet</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
